import type { RemoteConfig } from "@repo/core-utils";
import {
  getCookie,
  getFingerprint,
  isTouchDevice,
  PlatformAPI,
  setCookie,
  uuidv4,
  validateUUID,
} from "@repo/core-utils";
import * as Sentry from "@sentry/browser";

import { onPageHide } from "./onPageHide";
import { onSessionPaused } from "./onSessionPaused";

const PROD_TELEMETRY_URL = "https://telemetry.api.shapr3d.com/web";
const STAGE_TELEMETRY_URL = "https://stage.telemetry.api.shapr3d.com/web";
/**
 * NOTE: To generate the telemetry schema run "npm run generate:telemetry"
 */
import {
  ElementClicked,
  ElementHovered,
  PageTranslated,
  PageVisited,
  SessionPaused,
} from "./web.schema";

const urlKeysToReplace = ["token", "hash"];

export interface TelemetryEvent {
  readonly eventProperties: Record<string, unknown>;
  readonly eventName: string;
}

function generateSessionID() {
  const id = sessionStorage.getItem(TelemetryClient.SESSION_ID_KEY);
  if (id && validateUUID(id)) {
    return id;
  }
  const newID = uuidv4();
  sessionStorage.setItem(TelemetryClient.SESSION_ID_KEY, newID);
  return newID;
}

export function getAndResetCookie(name: string, ttlHours: number, domain: string) {
  const cookie = getCookie(name);
  if (cookie && validateUUID(cookie)) {
    setCookie(name, cookie, ttlHours, domain); // Reseting ttl
    return cookie;
  }
  const newCookie = uuidv4();
  setCookie(name, newCookie, ttlHours, domain);
  return newCookie;
}

function getCleanedUrl(location: Location) {
  const search = getCleanedSearchParams(location);
  const hash = getCleanedHashParams(location);
  return `${location.origin}${location.pathname}${search}${hash}`;
}

function getCleanedSearchParams(location: Location) {
  const searchParams = new URLSearchParams(location.search);
  for (const key of urlKeysToReplace) {
    if (searchParams.has(key)) {
      searchParams.set(key, "hidden");
    }
  }
  return searchParams.toString().length ? `?${searchParams.toString()}` : "";
}

function getCleanedHashParams(location: Location) {
  const hashParams = new URLSearchParams(location.hash.replace("#", "?"));
  for (const key of urlKeysToReplace) {
    if (hashParams.has(key)) {
      hashParams.set(key, "hidden");
    }
  }
  return hashParams.toString().length ? `#${hashParams.toString()}` : "";
}

export enum TelemetryClientEnvironment {
  PROD = "prod",
  BETA = "beta",
  STAGE = "stage",
  DEV = "dev",
}

export interface TelemetryClientProps {
  appName: string;
  siteVersion: string;
  environment: TelemetryClientEnvironment;
  cookieDomainOverride?: string;
  telemetryUrlOverride?: string;
  userId?: string | null;
  titleOverride?: string;
}

export class TelemetryClient {
  static TRACKING_ID_COOKIE_NAME = "t_id";
  static SESSION_ID_KEY = "s_id";
  private userId: string | null;
  private readonly siteVersion: string;
  private readonly telemetryUrl: string = STAGE_TELEMETRY_URL;
  private readonly appName: string;
  private readonly titleOverride: string | undefined;
  private readonly cookieDomain: string;
  private readonly cookiesEnabled: boolean;
  private experiments: RemoteConfig = {};
  private locale: string | null = null;

  constructor({
    appName,
    siteVersion,
    cookieDomainOverride,
    telemetryUrlOverride,
    titleOverride,
    userId,
    environment,
  }: TelemetryClientProps) {
    if (environment === TelemetryClientEnvironment.PROD) {
      this.telemetryUrl = PROD_TELEMETRY_URL;
    }
    if (telemetryUrlOverride) {
      this.telemetryUrl = telemetryUrlOverride;
    }
    this.titleOverride = titleOverride;
    this.siteVersion = siteVersion;
    this.appName = appName;
    this.cookieDomain =
      environment === TelemetryClientEnvironment.DEV ? "localhost" : "shapr3d.com";
    if (cookieDomainOverride) {
      this.cookieDomain = cookieDomainOverride;
    }
    this.cookiesEnabled = this.isCookiesEnabled();
    this.userId = userId ?? null;
    Sentry.setUser({ id: userId ?? undefined });
  }

  private isCookiesEnabled() {
    const TEST_COOKIE_NAME = "shaprtestcookie";
    setCookie(TEST_COOKIE_NAME, "1", 1, this.cookieDomain);
    const cookie = getCookie(TEST_COOKIE_NAME);
    setCookie(TEST_COOKIE_NAME, "", 0, this.cookieDomain);
    return cookie != null;
  }

  public setUserId(userId: string | null) {
    this.userId = userId;
    Sentry.setUser({ id: userId ?? undefined });
  }

  public setLocale(locale: string | null) {
    this.locale = locale;
  }

  public getTrackingID() {
    const trackingID = getAndResetCookie(
      TelemetryClient.TRACKING_ID_COOKIE_NAME,
      24 * 182,
      this.cookieDomain
    );
    Sentry.setTag("trackingID", trackingID);
    return trackingID;
  }

  public resetTrackingID() {
    setCookie(TelemetryClient.TRACKING_ID_COOKIE_NAME, "", 0, this.cookieDomain);
    return this.getTrackingID();
  }

  public trackTelemetryEvents(telemetryEvents: TelemetryEvent[]) {
    try {
      const events: Record<string, unknown>[] = telemetryEvents.map((telemetryEvent) => ({
        event_id: uuidv4(),
        app_name: this.appName,
        client_version: this.siteVersion,
        event_name: telemetryEvent.eventName,
        event_properties: telemetryEvent.eventProperties,
        event_time: new Date().toISOString(),
        experiments: this.experiments,
        hash: getCleanedHashParams(window.location),
        page_title: this.titleOverride ?? document.title,
        session_id: generateSessionID(),
        site_version: this.siteVersion,
        tracking_id: this.getTrackingID(),
        browser_fp_id: getFingerprint(),
        url: getCleanedUrl(window.location),
        user_agent: window.navigator.userAgent,
        user_id: this.userId ?? undefined,
        locale: this.locale ?? undefined,
        cookies_enabled: this.cookiesEnabled,
        visibility_state: document.visibilityState,
        platform: PlatformAPI.getInstance().getPlatform(),
        os_name: PlatformAPI.getInstance().getOSName(),
        os_version: PlatformAPI.getInstance().getOSVersion(),
        browser_name: PlatformAPI.getInstance().getBrowserName(),
        browser_version: PlatformAPI.getInstance().getBrowserVersion(),
        is_touch_device: isTouchDevice(),
        viewport_width: window.screen.width,
        viewport_height: window.screen.height,
      }));
      this.sendRequest(events);
    } catch (err) {
      console.warn(`Couldn't track event ${telemetryEvents}:\n ${err}`);
    }
  }

  public trackTelemetryEvent(telemetryEvent: TelemetryEvent) {
    this.trackTelemetryEvents([telemetryEvent]);
  }

  public setExperiments(experiments: RemoteConfig) {
    this.experiments = Object.entries(experiments).reduce(
      (acc, [key, value]) => ({ ...acc, [key]: value?.toString() ?? "" }),
      {}
    );
  }

  private sendRequest(evt: Record<string, unknown>[]) {
    navigator.sendBeacon(this.telemetryUrl, JSON.stringify(evt));
  }
}

export class TelemetryUtils {
  private readonly client: TelemetryClient;
  private readonly referrer: string;

  constructor(client: TelemetryClient, referrer = "") {
    this.client = client;
    this.referrer = referrer;
  }

  private processSymbolOverrides(telemetrySelector: string) {
    const overrideFields = document.querySelectorAll(
      "[data-telemetry-override]"
    ) as NodeListOf<HTMLElement>;
    overrideFields.forEach((field) => {
      if (field.dataset) {
        const overrideScope = field.parentElement;
        const overrideValue = field.innerHTML;
        const overrideAttribute = field.dataset.telemetryOverride;
        if (overrideScope && overrideAttribute && overrideValue) {
          const trackedElements = overrideScope.querySelectorAll(
            telemetrySelector
          ) as NodeListOf<HTMLElement>;
          if (trackedElements) {
            trackedElements.forEach((element) => {
              element.dataset[overrideAttribute] = overrideValue;
            });
          }
        }
      }
    });
  }

  public trackClicks(telemetrySelector: string) {
    this.processSymbolOverrides(telemetrySelector);

    const elements = document.querySelectorAll(telemetrySelector) as NodeListOf<HTMLElement>;
    elements.forEach((element) => {
      element.addEventListener("click", () => {
        this.client.trackTelemetryEvent(
          new ElementClicked({
            elementId: element.id,
            action: element?.dataset?.action,
            location: element?.dataset?.location,
            details: element?.dataset?.details,
          })
        );
      });
    });
  }

  public trackHovers(telemetrySelector: string) {
    this.processSymbolOverrides(telemetrySelector);

    const elements = document.querySelectorAll(telemetrySelector) as NodeListOf<HTMLElement>;
    elements.forEach((element) => {
      element.addEventListener("mouseenter", () => {
        this.client.trackTelemetryEvent(
          new ElementHovered({
            action: element?.dataset?.action,
            location: element?.dataset?.location,
            details: element?.dataset?.details,
          })
        );
      });
    });
  }

  public trackPageViews() {
    this.client.trackTelemetryEvent(new PageVisited({ referrer: this.referrer }));
  }

  public trackSession() {
    onSessionPaused(({ duration }) => {
      this.client.trackTelemetryEvent(new SessionPaused({ duration }));
    });
  }

  public trackMachineTranslation({ defaultLanguage } = { defaultLanguage: "en" }) {
    onPageHide(() => {
      const isPageTranslated = Boolean(
        document.querySelector(
          "html.translated-ltr, html.translated-rtl, ya-tr-span, *[_msttexthash], *[x-bergamot-translated]"
        )
      );
      const lang = document.documentElement.lang;
      if (isPageTranslated || lang != defaultLanguage) {
        this.client.trackTelemetryEvent(new PageTranslated({ lang }));
      }
    });
  }
}

export default TelemetryUtils;
